export const ONBOARDING_STORAGE_KEY = 'checkoutOnboardingData';

const TYPED_ONBOARDING_KEYS = new Set([
    'sex',
    'age',
    'heightValue',
    'heightUnit',
    'heightFt',
    'heightIn',
    'weightValue',
    'weightUnit',
    'goalWeightValue',
    'dietMode',
    'dietType',
    'dietId',
    'activityLevel',
    'spontaneousActivity',
    'spontaneousActivityLevel',
    'muscleGroups',
    'workoutLocation',
    'motivations',
    'nutritionPreferences',
    'dietSource',
    'strengthWorkoutsExperience',
    'strengthTrainingExperience',
    'hashimotoConditions',
    'hashimotoSymptoms',
    'hashimotoGlp1Usage',
    'preferredInstructors',
    'exclusions',
    'tshValue',
    'fastingDietProgramme',
    'fastType',
    'fastDays',
    'fastPreparationDays',
    'fastDayOfTheWeek',
    'regularActivityLevel',
    'fastingDietType',
]);

const MAX_CUSTOM_ATTRIBUTE_KEY_LENGTH = 128;

const VALID_DIET_MODES = new Set([
    'slimming',
    'stabilization',
    'put_on_weight',
    'gain_muscle_mass',
]);

const VALID_FASTING_DIET_PROGRAMMES = new Set([
    'full',
    'fastPreparation',
    'fast',
    'fastExit',
    'regular',
]);

const VALID_FAST_TYPES = new Set(['FAST_TYPE_CLASSIC', 'FAST_TYPE_SIMPLE']);

const VALID_FASTING_DIET_TYPES = new Set([
    'mediterranean',
    'elimination',
    'vegetarian',
]);

const VALID_REGULAR_ACTIVITY_LEVELS = new Set(['low', 'medium', 'high']);

const DAY_OF_THE_WEEK_BY_INDEX = [
    'sun',
    'mon',
    'tue',
    'wed',
    'thu',
    'fri',
    'sat',
];

export const decodeOnboardingData = encoded => {
    try {
        const base = encoded.split('|')[0];
        const normalized = base.replace(/ /g, '+');
        return JSON.parse(atob(normalized));
    } catch (e) {
        return null;
    }
};

const buildHeight = data => {
    if (data.heightUnit === 'in') {
        const totalInches = (data.heightFt || 0) * 12 + (data.heightIn || 0);
        return totalInches > 0 ? { value: totalInches, unit: 'in' } : undefined;
    }
    return data.heightValue && data.heightValue > 0
        ? { value: data.heightValue, unit: 'cm' }
        : undefined;
};

const buildWeight = (value, unit) => {
    if (!value || value <= 0) return undefined;
    return { value, unit: unit === 'lb' ? 'lb' : 'kg' };
};

const toInt = value => {
    if (value === undefined || value === null || value === '') return undefined;
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
};

const toCustomAttribute = (key, value) => {
    if (Array.isArray(value)) {
        const strings = value.filter(
            item => typeof item === 'string' && item !== ''
        );
        return strings.length ? { key, listValue: strings } : null;
    }
    if (typeof value === 'boolean') return { key, boolValue: value };
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) return null;
        return Number.isInteger(value)
            ? { key, intValue: value }
            : { key, floatValue: value };
    }
    if (typeof value === 'string') {
        return value ? { key, stringValue: value } : null;
    }
    return null;
};

const buildCustomAttributes = data =>
    Object.entries(data)
        .filter(
            ([key]) =>
                !TYPED_ONBOARDING_KEYS.has(key) &&
                key &&
                key.length <= MAX_CUSTOM_ATTRIBUTE_KEY_LENGTH
        )
        .map(([key, value]) => toCustomAttribute(key, value))
        .filter(attribute => attribute !== null);

export const mapOnboardingToSaveVariables = data => {
    const variables = {};

    if (data.sex === 'male' || data.sex === 'female') {
        variables.sex = data.sex;
    }
    if (data.age && data.age > 0) {
        variables.age = data.age;
    }
    if (data.dietMode && VALID_DIET_MODES.has(data.dietMode)) {
        variables.dietMode = data.dietMode;
    }

    const height = buildHeight(data);
    if (height) variables.height = height;

    const weight = buildWeight(data.weightValue, data.weightUnit);
    if (weight) variables.weight = weight;

    const goalWeight = buildWeight(data.goalWeightValue, data.weightUnit);
    if (goalWeight) variables.goalWeight = goalWeight;

    if (data.dietType) variables.dietType = data.dietType;

    const dietId = toInt(data.dietId);
    if (dietId !== undefined) variables.dietId = dietId;

    if (data.activityLevel) variables.activityLevel = data.activityLevel;
    if (data.spontaneousActivityLevel) {
        variables.spontaneousActivityLevel = data.spontaneousActivityLevel;
    }
    if (data.muscleGroups && data.muscleGroups.length) {
        variables.muscleGroups = data.muscleGroups;
    }
    if (data.workoutLocation && data.workoutLocation.length) {
        variables.workoutLocation = data.workoutLocation;
    }
    if (data.motivations && data.motivations.length) {
        variables.motivations = data.motivations;
    }
    if (data.nutritionPreferences && data.nutritionPreferences.length) {
        variables.nutritionPreferences = data.nutritionPreferences;
    }
    if (data.dietSource) variables.dietSource = data.dietSource;
    if (data.strengthTrainingExperience) {
        variables.strengthTrainingExperience = data.strengthTrainingExperience;
    }
    if (data.hashimotoConditions && data.hashimotoConditions.length) {
        variables.hashimotoConditions = data.hashimotoConditions;
    }
    if (data.hashimotoSymptoms && data.hashimotoSymptoms.length) {
        variables.hashimotoSymptoms = data.hashimotoSymptoms;
    }
    if (data.hashimotoGlp1Usage) {
        variables.hashimotoGlp1Usage = data.hashimotoGlp1Usage;
    }
    if (data.preferredInstructors && data.preferredInstructors.length) {
        variables.preferredInstructors = data.preferredInstructors;
    }
    if (data.exclusions && data.exclusions > 0) {
        variables.exclusions = data.exclusions;
    }
    if (data.tshValue && data.tshValue > 0) {
        variables.hashimotoTsh = data.tshValue;
    }

    if (
        data.fastingDietProgramme &&
        VALID_FASTING_DIET_PROGRAMMES.has(data.fastingDietProgramme)
    ) {
        variables.fastingDietProgramme = data.fastingDietProgramme;
    }
    if (data.fastType && VALID_FAST_TYPES.has(data.fastType)) {
        variables.fastType = data.fastType;
    }
    const fastDays = toInt(data.fastDays);
    if (fastDays !== undefined && fastDays >= 1 && fastDays <= 14) {
        variables.fastDays = fastDays;
    }
    const fastPreparationDays = toInt(data.fastPreparationDays);
    if (
        fastPreparationDays !== undefined &&
        fastPreparationDays >= 1 &&
        fastPreparationDays <= 14
    ) {
        variables.fastPreparationDays = fastPreparationDays;
    }
    if (
        data.fastingDietType &&
        VALID_FASTING_DIET_TYPES.has(data.fastingDietType)
    ) {
        variables.fastingDietType = data.fastingDietType;
    }
    const fastDayOfTheWeekIndex = toInt(data.fastDayOfTheWeek);
    if (
        fastDayOfTheWeekIndex !== undefined &&
        fastDayOfTheWeekIndex >= 0 &&
        fastDayOfTheWeekIndex <= 6
    ) {
        variables.fastDayOfTheWeek =
            DAY_OF_THE_WEEK_BY_INDEX[fastDayOfTheWeekIndex];
    }
    if (
        data.regularActivityLevel &&
        VALID_REGULAR_ACTIVITY_LEVELS.has(data.regularActivityLevel)
    ) {
        variables.regularActivityLevel = data.regularActivityLevel;
    }

    const customAttributes = buildCustomAttributes(data);
    if (customAttributes.length) variables.customAttributes = customAttributes;

    return variables;
};
